import { getIconsUrl } from '@orionprotocol/env';
import isSvg from 'is-svg';

type CacheState =
  | {
      state: 'loading';
      promise: Promise<string>;
    }
  | {
      state: 'loaded';
      icon: string;
    };

type IconLoader = {
  load(collection: string, actualIcon: string): CacheState;
};

export function createIconLoader(): IconLoader {
  const cache = new Map<string, CacheState>();
  const loadedIcons = new Map<string, string>();
  const iconsURL = getIconsUrl();

  function load(collection: string, actualIcon: string): CacheState {
    const currentURL = collection === 'ui' ? '/icons/' : iconsURL;

    const blankasset = `<img src="${currentURL}currency/blankasset.svg" alt="blank asset" />`;

    if (actualIcon.length === 0) {
      return {
        state: 'loaded',
        icon: blankasset,
      };
    }

    const basePath = `${currentURL}${collection}/${actualIcon}`;
    const pathSVG = `${basePath}.svg`;
    const pathPNG = `${basePath}.png`;
    let finalPath = pathSVG;

    const cacheState = cache.get(finalPath);

    if (cacheState) {
      return cacheState;
    }

    function getPngPromise(): Promise<string> {
      return fetch(pathPNG)
        .then(async (res) => {
          if (!res.ok) {
            console.error(
              'PNG icon could not be loaded:',
              finalPath,
              res.text(),
            );

            return blankasset;
          }

          finalPath = pathPNG;
          const blob = await res.blob();
          const imgUrl = URL.createObjectURL(blob);
          const img = `<img src='${imgUrl}' alt="icon" />`;

          return img;
        })
        .catch((pngErr) => {
          console.error('Error loading PNG icon:', finalPath, pngErr);

          return blankasset;
        });
    }

    const loadingPromise = fetch(pathSVG)
      .then((res) => {
        if (!res.ok) {
          return getPngPromise();
        }

        return res.text();
      })
      .then((rawImg) => {
        if (isSvg(rawImg)) {
          loadedIcons.set(finalPath, rawImg);

          cache.set(finalPath, { state: 'loaded', icon: rawImg });
          return rawImg;
        }

        return getPngPromise();
      })
      .catch((err) => {
        console.log('SVG icon could not be loaded:', finalPath, err);

        return getPngPromise();
      });

    const loadingState: CacheState = {
      state: 'loading',
      promise: loadingPromise,
    };

    cache.set(finalPath, loadingState);

    return loadingState;
  }

  return {
    load,
  };
}
